import { z } from 'zod';
import type { SamlContact } from '@galapagos/shared';
import {
  ensureAuthTeardownParticipant,
  type AuthTeardownParticipant,
} from '@/services/auth-teardown';

/**
 * The sealed employer-SSO session between `/saml` and the SAML eligibility
 * check, shared by the MVP registration pages and the signup flow's SSO
 * steps. The handle is server-encrypted, so it is safe in sessionStorage; the
 * contacts are masked display strings only. Tab-scoped by design: a closed tab
 * drops it and the member continues on the demographic path.
 */
export interface StoredSamlSession {
  readonly session: string;
  readonly contacts: Partial<Record<SamlContact, { readonly masked: string }>>;
  /**
   * Whether the roster row carries a DOB. Absent (an older stored value) is
   * read as false, so the member is asked rather than the check refused.
   */
  readonly hasRosterDob?: boolean;
  /** Set once the member signed in through this session's roster contact. */
  readonly signedInThrough?: boolean;
  /** The roster contact the code was sent to, so a reload on the code step keeps it. */
  readonly chosenContact?: SamlContact;
  /**
   * Set while the employer check from this session has gone and not failed,
   * so a reload does not send it a second time.
   */
  readonly checkSent?: boolean;
}

export const SAML_SESSION_STORAGE_KEY = 'galapagos.reg.saml_session';

const storedSamlSessionSchema = z.object({
  session: z.string().min(1),
  contacts: z
    .object({
      email: z.object({ masked: z.string() }).optional(),
      phone: z.object({ masked: z.string() }).optional(),
    })
    .strict(),
  hasRosterDob: z.boolean().optional(),
  signedInThrough: z.boolean().optional(),
  chosenContact: z.enum(['email', 'phone']).optional(),
  checkSent: z.boolean().optional(),
});

export function readSamlSession(): StoredSamlSession | null {
  try {
    const raw = window.sessionStorage.getItem(SAML_SESSION_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = storedSamlSessionSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function storeSamlSession(value: StoredSamlSession): void {
  try {
    window.sessionStorage.setItem(SAML_SESSION_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage blocked: the flow continues on the demographic path.
  }
}

/** Record that the member signed in through the stored session's roster contact. */
export function markSamlSessionSignedIn(): void {
  updateSamlSession({ signedInThrough: true });
}

/** Merge `patch` into the stored session; nothing happens when there is none. */
export function updateSamlSession(
  patch: Partial<Pick<StoredSamlSession, 'signedInThrough' | 'chosenContact' | 'checkSent'>>,
): void {
  const stored = readSamlSession();
  if (stored) storeSamlSession({ ...stored, ...patch });
}

export function clearSamlSession(): void {
  try {
    window.sessionStorage.removeItem(SAML_SESSION_STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unreadable.
  }
}

/**
 * Which kind of roster contact (email or phone) an SSO member signed in at,
 * kept for the tab apart from the session: a refused employer check drops the
 * session, but the signup still needs to know which channel to ask for next.
 * Only the kind is kept, never the contact.
 */
export const SAML_SIGNED_IN_CONTACT_KEY = 'galapagos.reg.saml_signed_in_contact';

export function rememberSamlSignedInContact(contact: SamlContact): void {
  try {
    window.sessionStorage.setItem(SAML_SIGNED_IN_CONTACT_KEY, contact);
  } catch {
    // Storage blocked: a reload asks for the email, as a typed sign-in does.
  }
}

export function readSamlSignedInContact(): SamlContact | null {
  try {
    const value = window.sessionStorage.getItem(SAML_SIGNED_IN_CONTACT_KEY);
    return value === 'email' || value === 'phone' ? value : null;
  } catch {
    return null;
  }
}

function forgetSamlSignedInContact(): void {
  try {
    window.sessionStorage.removeItem(SAML_SIGNED_IN_CONTACT_KEY);
  } catch {
    // Nothing to forget when storage is unreadable.
  }
}

/** The masked contacts the session offers, in display order. */
export function samlContactOptions(
  stored: StoredSamlSession,
): { contact: SamlContact; masked: string }[] {
  const order: SamlContact[] = ['email', 'phone'];
  return order.flatMap((contact) => {
    const entry = stored.contacts[contact];
    return entry ? [{ contact, masked: entry.masked }] : [];
  });
}

/** A session boundary drops the employer-SSO handle with the rest of the member's state. */
export const samlSessionTeardownParticipant: AuthTeardownParticipant = {
  id: 'saml-session-store',
  clear: () => {
    clearSamlSession();
    forgetSamlSignedInContact();
  },
};

ensureAuthTeardownParticipant(samlSessionTeardownParticipant);
